import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { getAuthenticatedUser } from "api/auth.api";
import { acceptInvitation, lookupInvitation } from "api/invitation.api";
import { QUERY_KEYS } from "config/queryKeys";
import { ROUTES } from "config/routes";

const PENDING_INVITE_TOKEN_KEY = "pendingInviteToken";

const getInvite = (data) => {
  return data?.data?.invitation || data?.invitation || data?.data || data;
};

const getLookupQueryKey = (token) => {
  if (QUERY_KEYS?.invitations?.lookup) {
    return QUERY_KEYS.invitations.lookup(token);
  }

  return ["invitations", "lookup", token];
};

const getAuthQueryKey = () => {
  if (QUERY_KEYS?.auth?.me) {
    return QUERY_KEYS.auth.me();
  }

  return ["auth", "me"];
};

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get("token");

  const [token] = React.useState(() => {
    return urlToken || sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY) || "";
  });

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (token) {
      sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
    }
  }, [token]);

  const {
    data: invitationData,
    isLoading: isInviteLoading,
    isError: isInviteError,
    error: inviteError,
  } = useQuery({
    queryKey: getLookupQueryKey(token),
    queryFn: () => lookupInvitation(token),
    enabled: Boolean(token),
    retry: false,
  });

  const {
    data: authData,
    isLoading: isAuthLoading,
    isError: isAuthError,
  } = useQuery({
    queryKey: getAuthQueryKey(),
    queryFn: getAuthenticatedUser,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { mutate: acceptInvite, isPending } = useMutation({
    mutationFn: () => acceptInvitation(token),

    onSuccess: async () => {
      sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);

      await queryClient.invalidateQueries({ queryKey: getAuthQueryKey() });
      await queryClient.invalidateQueries({ queryKey: ["companies"] });
      await queryClient.invalidateQueries({ queryKey: ["memberships"] });
      await queryClient.invalidateQueries({ queryKey: ["invitations"] });

      toast.success("Invitation accepted.");
      navigate(ROUTES.COMPANY_SELECT, { replace: true });
    },

    onError: (error) => {
      const status = error?.status || error?.response?.status;

      if (status === 403) {
        toast.error("This invitation was sent to a different email address.");
        return;
      }

      toast.error(error?.message || "Failed to accept invitation.");
    },
  });

  const invitation = getInvite(invitationData);
  const isAuthenticated = Boolean(authData?.user) && !isAuthError;

  if (!token) {
    return (
      <InviteShell>
        <ErrorState
          title="Invalid invitation link"
          message="This invitation link is missing a token."
        />
      </InviteShell>
    );
  }

  if (isInviteLoading || isAuthLoading) {
    return (
      <InviteShell>
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4" />
          <p className="text-sm text-muted-foreground">Loading invitation...</p>
        </div>
      </InviteShell>
    );
  }

  if (isInviteError) {
    return (
      <InviteShell>
        <ErrorState
          title="Could not load invitation"
          message={
            inviteError?.message || "This invitation may be invalid or expired."
          }
        />
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <div className="w-full max-w-lg bg-card border border-border rounded-xl p-6 shadow-sm">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Icon name="Mail" size={24} className="text-primary" />
        </div>

        <h1 className="text-2xl font-bold text-center text-foreground mb-2">
          You&apos;ve been invited
        </h1>

        <p className="text-sm text-center text-muted-foreground mb-6">
          Review the invitation details below.
        </p>

        <div className="space-y-4 bg-muted/30 rounded-lg p-4 mb-6">
          <InviteRow
            label="Company"
            value={
              invitation?.companyName ||
              invitation?.company?.legalName ||
              invitation?.company?.name ||
              "N/A"
            }
          />

          <InviteRow
            label="Inviter"
            value={
              invitation?.inviterName ||
              invitation?.inviter?.name ||
              invitation?.inviter?.email ||
              invitation?.invitedBy?.name ||
              invitation?.invitedBy?.email ||
              "N/A"
            }
          />

          <InviteRow
            label="Invited Email"
            value={invitation?.email || invitation?.invitedEmail || "N/A"}
          />

          <InviteRow
            label="Company Role"
            value={formatRole(invitation?.companyRole)}
          />

          <InviteRow
            label="Expires"
            value={formatDate(invitation?.expiresAt)}
          />

          {Array.isArray(invitation?.branchRoles) &&
            invitation.branchRoles.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Branches</p>

                <div className="space-y-2">
                  {invitation.branchRoles.map((branchRole, index) => {
                    const branch =
                      typeof branchRole.branchId === "object"
                        ? branchRole.branchId
                        : null;

                    const branchLabel =
                      branch?.name ||
                      branchRole.branchName ||
                      branchRole.branchCode ||
                      (branchRole.branchId
                        ? String(branchRole.branchId)
                        : "Branch");

                    return (
                      <div
                        key={`${branch?._id || branchRole.branchId || index}`}
                        className="text-sm text-foreground bg-background border border-border rounded-lg px-3 py-2"
                      >
                        <span className="font-medium">{branchLabel}</span>
                        <span className="text-muted-foreground">
                          {" "}
                          — {formatRole(branchRole.branchRole)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>

        {!isAuthenticated ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              Login or create an account with the invited email address to
              accept this invitation.
            </p>

            <Button
              className="w-full"
              onClick={() =>
                navigate(ROUTES.LANDING, {
                  state: {
                    pendingInviteToken: token,
                    returnTo: ROUTES.ACCEPT_INVITE,
                  },
                })
              }
            >
              Go to login
            </Button>
          </div>
        ) : (
          <Button
            className="w-full"
            onClick={() => acceptInvite()}
            disabled={isPending}
          >
            {isPending ? "Accepting..." : "Accept Invitation"}
          </Button>
        )}
      </div>
    </InviteShell>
  );
};

const formatRole = (role) => {
  if (!role) return "N/A";

  return role
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDate = (date) => {
  if (!date) return "N/A";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "N/A";
  }

  return parsedDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const InviteShell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
    {children}
  </div>
);

const InviteRow = ({ label, value }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-medium text-foreground">{value}</p>
  </div>
);

const ErrorState = ({ title, message }) => (
  <div className="w-full max-w-md bg-card border border-border rounded-xl p-6 text-center">
    <Icon
      name="AlertTriangle"
      size={40}
      className="mx-auto text-red-500 mb-4"
    />

    <h1 className="text-xl font-semibold text-foreground mb-2">{title}</h1>

    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

export default AcceptInvite;
